/**
 * Media Permissions Flow
 * Complete permission flow orchestrator
 * Handles education → request → blocked recovery
 */

import React, { useEffect, useRef } from 'react'
import { useMediaPermissions } from 'src/customHooks/mediaPermissions'
import { PermissionEducationDialog } from './PermissionEducationDialog'
import { PermissionRequestDialog } from './PermissionRequestDialog'
import { PermissionBlockedDialog } from './PermissionBlockedDialog'
import { PermissionPartialDialog } from './PermissionPartialDialog'
import { PERMISSION_STATES } from 'src/services/mediaPermissions'

/**
 * MediaPermissionsFlow Component
 * 
 * @param {Object} props
 * @param {string} props.mediaType - 'camera', 'microphone', or 'both'
 * @param {boolean} props.autoStart - Start permission flow automatically
 * @param {function} props.onSuccess - Callback when permissions granted
 * @param {function} props.onFailure - Callback when permissions denied
 * @param {function} props.onSkip - Callback when user skips
 * @param {string} props.sessionType - Session type for tracking
 * @param {boolean} props.showSkipOption - Show skip button in education dialog
 */
export const MediaPermissionsFlow = ({
  mediaType = 'both',
  autoStart = false,
  autoCheck = true, // Allow parent to control auto-check
  onSuccess = null,
  onFailure = null,
  onSkip = null,
  sessionType = 'general',
  showSkipOption = false,
}) => {
  const {
    cameraPermission,
    microphonePermission,
    isGranted,
    isDenied,
    isRequesting,
    showEducation,
    showBlockedDialog,
    recoveryPlan,
    requestAttempts,
    startPermissionFlow,
    proceedAfterEducation,
    retryPermissions,
    setShowEducation,
    setShowBlockedDialog,
  } = useMediaPermissions({
    mediaType,
    autoCheck: autoCheck, // Use prop instead of hardcoded true
    sessionType,
  })

  // Auto-start flow if requested
  useEffect(() => {
    if (autoStart && !isGranted && !showEducation) {
      startPermissionFlow()
    }
  }, [autoStart, isGranted, showEducation, startPermissionFlow])

  // Handle success - use ref to avoid dependency issues
  const onSuccessRef = React.useRef(onSuccess)
  const hasCalledSuccessRef = React.useRef(false)
  React.useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])
  
  useEffect(() => {
    if (isGranted && onSuccessRef.current && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true
      // Use setTimeout to ensure callback runs after render phase
      // This prevents "Cannot update component during render" warnings
      setTimeout(() => {
        onSuccessRef.current({
          camera: cameraPermission,
          microphone: microphonePermission,
        })
      }, 0)
    } else if (!isGranted) {
      // Reset flag when permissions are revoked
      hasCalledSuccessRef.current = false
    }
  }, [isGranted, cameraPermission, microphonePermission])

  // Handle failure - use ref to avoid dependency issues
  const onFailureRef = React.useRef(onFailure)
  const hasCalledFailureRef = React.useRef(false)
  React.useEffect(() => {
    onFailureRef.current = onFailure
  }, [onFailure])
  
  useEffect(() => {
    if (isDenied && onFailureRef.current && showBlockedDialog && !hasCalledFailureRef.current) {
      hasCalledFailureRef.current = true
      // Use setTimeout to ensure callback runs after render phase
      // This prevents "Cannot update component during render" warnings
      setTimeout(() => {
        onFailureRef.current({
          camera: cameraPermission,
          microphone: microphonePermission,
        })
      }, 0)
    } else if (!isDenied || !showBlockedDialog) {
      // Reset flag when permissions are no longer denied or dialog closes
      hasCalledFailureRef.current = false
    }
  }, [isDenied, cameraPermission, microphonePermission, showBlockedDialog])

  const handleEducationAllow = async () => {
    const result = await proceedAfterEducation()
    
    // If permission was denied, blocked dialog will show automatically
    // If granted, onSuccess callback will fire
  }

  const handleEducationSkip = () => {
    setShowEducation(false)
    setShowBlockedDialog(false)
    if (onSkip) {
      onSkip()
    }
  }

  const handleBlockedSkip = () => {
    setShowBlockedDialog(false)
    if (onSkip) {
      onSkip()
    }
  }

  const handleRetry = async () => {
    await retryPermissions()
  }

  // Check for partial permissions (one granted, one denied)
  const hasPartialPermissions =
    mediaType === 'both' &&
    ((cameraPermission === PERMISSION_STATES.GRANTED &&
      microphonePermission === PERMISSION_STATES.DENIED) ||
      (cameraPermission === PERMISSION_STATES.DENIED &&
        microphonePermission === PERMISSION_STATES.GRANTED))

  const getGrantedPermission = () => {
    if (cameraPermission === PERMISSION_STATES.GRANTED) return 'camera'
    if (microphonePermission === PERMISSION_STATES.GRANTED) return 'microphone'
    return null
  }

  const getDeniedPermission = () => {
    if (cameraPermission === PERMISSION_STATES.DENIED) return 'camera'
    if (microphonePermission === PERMISSION_STATES.DENIED) return 'microphone'
    return null
  }

  return (
    <>
      {/* Education Dialog */}
      <PermissionEducationDialog
        open={showEducation}
        onClose={handleEducationSkip}
        onAllow={handleEducationAllow}
        mediaType={mediaType}
        showSkip={showSkipOption}
      />

      {/* Request in Progress Dialog */}
      <PermissionRequestDialog open={isRequesting} mediaType={mediaType} />

      {/* Blocked Dialog */}
      <PermissionBlockedDialog
        open={showBlockedDialog && !hasPartialPermissions}
        onClose={handleBlockedSkip}
        onRetry={handleRetry}
        recoveryPlan={recoveryPlan}
        attemptCount={requestAttempts}
        showSupport={requestAttempts > 1}
      />

      {/* Partial Permissions Dialog */}
      {hasPartialPermissions && (
        <PermissionPartialDialog
          open={showBlockedDialog}
          onClose={handleBlockedSkip}
          onProceed={() => {
            setShowBlockedDialog(false)
            if (onSuccess) {
              onSuccess({
                camera: cameraPermission,
                microphone: microphonePermission,
              })
            }
          }}
          onRetry={handleRetry}
          grantedPermission={getGrantedPermission()}
          deniedPermission={getDeniedPermission()}
        />
      )}
    </>
  )
}

export default MediaPermissionsFlow

